import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const field = "mt-1.5 bg-slate-900 border-slate-800 text-white";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [resetId, setResetId] = useState(null);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) { return undefined; }
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const run = async (fn) => {
    setError("");
    setLoading(true);
    try { await fn(); } catch (e) { setError(apiError(e, "Something went wrong")); }
    setLoading(false);
  };

  const sendCode = (e) => {
    e?.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim(), previous_reset_id: resetId });
      setResetId(data.reset_id);
      setCode("");
      setWait(60);
      toast.success(resetId ? "A new code is on its way" : "If the email is registered, a code is on its way");
    });
  };

  const reset = (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== pw2) return setError("Passwords do not match");
    run(async () => {
      await api.post("/auth/reset-password", { reset_id: resetId, code, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <img src="/logo.png" alt="Al Arabia" className="h-16 w-auto object-contain mb-8" />
        <h1 className="font-display text-2xl font-bold text-white tracking-tight">Forgot password</h1>
        <p className="text-sm text-slate-400 mt-1 mb-6">
          {resetId ? "Enter the code we emailed and choose a new password." : "We'll email you a 6-digit code to reset it."}
        </p>
        {!resetId ? (
          <form onSubmit={sendCode} className="space-y-4" data-testid="forgot-email-form">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ae" className={field} data-testid="forgot-email" />
            </div>
            {error && <ErrorBox msg={error} />}
            <Button type="submit" disabled={loading} className="w-full" data-testid="forgot-send-code">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset code"}
            </Button>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-4" data-testid="reset-form">
            <div className="flex items-start gap-3 text-sm text-slate-300 bg-slate-900/60 border border-slate-800 rounded-lg p-3">
              <MailCheck className="w-4 h-4 mt-0.5 text-primary shrink-0" />
              <span data-testid="reset-sent-to">If <span className="font-mono text-white">{email}</span> is registered, a code was sent. It expires in 15 minutes.</span>
            </div>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="reset-code">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="w-11 h-12 text-lg text-white border-slate-700 bg-slate-900" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">New password</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters"
                className={field} data-testid="reset-new-password" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Confirm new password</Label>
              <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={field} data-testid="reset-confirm-password" />
            </div>
            {error && <ErrorBox msg={error} />}
            <Button type="submit" disabled={loading} className="w-full" data-testid="reset-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
            <button type="button" onClick={sendCode} disabled={loading || wait > 0} className="text-xs text-primary disabled:text-slate-600 hover:underline" data-testid="reset-resend">
              {wait > 0 ? `Send a new code in ${wait}s` : "Send a new code"}
            </button>
          </form>
        )}
        <Link to="/login" className="mt-6 flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors" data-testid="back-to-login">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}

const ErrorBox = ({ msg }) => (
  <div className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900 rounded-md px-3 py-2" data-testid="forgot-error">{msg}</div>
);
